import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  Alert, CircularProgress, IconButton, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import SaveIcon from '@mui/icons-material/Save';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../components/ImageUpload';
import { QRCodeSVG } from 'qrcode.react';
import { invalidateQRLogoCache, invalidateAltLogoCache } from '../hooks/useQRLogo';
import SlideshowIcon from '@mui/icons-material/Slideshow';

export default function CompanySettings() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = userProfile?.isAdmin || userProfile?.role === 'superadmin' || userProfile?.role === 'vd';

  const [form, setForm] = useState({
    name: '',
    orgNumber: '',
    address: '',
    website: '',
    phone: '',
    email: '',
    description: '',
    logo: '',
    certLogo: '',
    qrLogoUrl: '',
    altLogo: '',
  });

  useEffect(() => {
    async function load() {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userSnap.exists()) { setLoading(false); return; }
      const companyId = userSnap.data().companyId;
      if (!companyId) {
        setError('Du är inte kopplad till något företag.');
        setLoading(false);
        return;
      }

      const [compSnap] = await Promise.all([
        getDoc(doc(db, 'companies', companyId)),
      ]);

      if (compSnap.exists()) {
        const data = compSnap.data();
        setCompany({ id: companyId, ...data });
        setForm({
          name: data.name || '',
          orgNumber: data.orgNumber || '',
          address: data.address || '',
          website: data.website || '',
          phone: data.phone || '',
          email: data.email || '',
          description: data.description || '',
          logo: data.logo || '',
          certLogo: data.certLogo || '',
          qrLogoUrl: data.qrLogoUrl || '',
          altLogo: data.altLogo || '',
        });
      }

      setLoading(false);
    }
    load();
  }, [currentUser]);

  async function handleSave() {
    if (!isAdmin) return;
    setSaving(true);
    await updateDoc(doc(db, 'companies', company.id), {
      ...form,
      updatedAt: new Date()
    });
    invalidateQRLogoCache();
    invalidateAltLogoCache();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Företagsinställningar
            </Typography>
          </Box>
          {isAdmin && (
            <Button variant="contained"
              startIcon={saving
                ? <CircularProgress size={16} color="inherit" />
                : <SaveIcon />}
              onClick={handleSave} disabled={saving}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              {saving ? 'Sparar...' : 'Spara'}
            </Button>
          )}
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3 }}>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Företagsuppgifter sparade!
          </Alert>
        )}
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {!isAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Du kan se företagsuppgifterna men bara admin kan redigera dem.
          </Alert>
        )}

        {/* LOGOTYPER */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Logotyper
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                Företagslogotyp (visas längst ner i offertens sidopanel)
              </Typography>
              {isAdmin ? (
                <ImageUpload
                  currentUrl={form.logo}
                  onUpload={url => setForm(prev => ({ ...prev, logo: url }))}
                  path={'companies/' + company?.id}
                  label="Ladda upp logotyp"
                />
              ) : (
                form.logo && (
                  <Box component="img" src={form.logo}
                    sx={{ height: 60, objectFit: 'contain', display: 'block' }} />
                )
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>
                Certifierings-/partnerlogotyp (visas i mitten av offertens sidopanel)
              </Typography>
              {isAdmin ? (
                <ImageUpload
                  currentUrl={form.certLogo}
                  onUpload={url => setForm(prev => ({ ...prev, certLogo: url }))}
                  path={'companies/' + company?.id + '/cert'}
                  label="Ladda upp certlogotyp"
                />
              ) : (
                form.certLogo && (
                  <Box component="img" src={form.certLogo}
                    sx={{ height: 60, objectFit: 'contain', display: 'block' }} />
                )
              )}
            </Box>

            {/* QR-logotyp */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <QrCodeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  QR-logotyp (visas i mitten av QR-koder)
                </Typography>
              </Box>
              {isAdmin ? (
                <Box>
                  <ImageUpload
                    currentUrl={form.qrLogoUrl}
                    onUpload={url => setForm(prev => ({ ...prev, qrLogoUrl: url }))}
                    path={'companies/' + company?.id + '/qr-logo'}
                    label={form.qrLogoUrl ? 'Byt QR-logotyp' : 'Ladda upp QR-logotyp'}
                  />
                  {form.qrLogoUrl && (
                    <Button size="small" onClick={() => setForm(prev => ({ ...prev, qrLogoUrl: '' }))}
                      sx={{ color: 'error.main', textTransform: 'none', mt: 0.5, display: 'block', p: 0, fontSize: '0.72rem' }}>
                      Ta bort
                    </Button>
                  )}
                </Box>
              ) : (
                form.qrLogoUrl && (
                  <Box component="img" src={form.qrLogoUrl}
                    sx={{ height: 40, objectFit: 'contain', display: 'block' }} />
                )
              )}
              {form.qrLogoUrl && (
                <Box sx={{ mt: 1.5, bgcolor: '#fff', p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', display: 'inline-block' }}>
                  <QRCodeSVG
                    value={`${window.location.origin}/workshop/DEMO`}
                    size={90} level="H"
                    imageSettings={{ src: form.qrLogoUrl, height: 22, width: 22, excavate: true }}
                  />
                </Box>
              )}
            </Box>

            {/* Presentationslogotyp */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <SlideshowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Presentationslogotyp (PitchN — cover och alla slides)
                </Typography>
              </Box>
              {isAdmin ? (
                <Box>
                  <ImageUpload
                    currentUrl={form.altLogo}
                    onUpload={url => setForm(prev => ({ ...prev, altLogo: url }))}
                    path={'companies/' + company?.id + '/alt-logo'}
                    label={form.altLogo ? 'Byt presentationslogotyp' : 'Ladda upp presentationslogotyp'}
                  />
                  {form.altLogo && (
                    <Button size="small" onClick={() => setForm(prev => ({ ...prev, altLogo: '' }))}
                      sx={{ color: 'error.main', textTransform: 'none', mt: 0.5, display: 'block', p: 0, fontSize: '0.72rem' }}>
                      Ta bort
                    </Button>
                  )}
                </Box>
              ) : (
                form.altLogo && (
                  <Box component="img" src={form.altLogo}
                    sx={{ height: 60, objectFit: 'contain', display: 'block' }} />
                )
              )}
              {form.altLogo && (
                <Box component="img" src={form.altLogo}
                  sx={{ mt: 1.5, height: 48, maxWidth: 240, objectFit: 'contain', display: 'block', opacity: 0.8 }} />
              )}
            </Box>
          </Box>
        </Paper>

        {/* FORETAGSUPPGIFTER */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Företagsuppgifter
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Företagsnamn" value={form.name} fullWidth
              disabled={!isAdmin}
              onChange={e => setForm(prev => ({
                ...prev, name: e.target.value }))} />
            <TextField label="Organisationsnummer" value={form.orgNumber}
              fullWidth disabled={!isAdmin}
              onChange={e => setForm(prev => ({
                ...prev, orgNumber: e.target.value }))} />
            <TextField label="Adress" value={form.address} fullWidth
              disabled={!isAdmin}
              onChange={e => setForm(prev => ({
                ...prev, address: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Webb" value={form.website} fullWidth
                disabled={!isAdmin}
                onChange={e => setForm(prev => ({
                  ...prev, website: e.target.value }))} />
              <TextField label="Telefon" value={form.phone} fullWidth
                disabled={!isAdmin}
                onChange={e => setForm(prev => ({
                  ...prev, phone: e.target.value }))} />
            </Box>
            <TextField label="E-post" value={form.email} fullWidth
              disabled={!isAdmin}
              onChange={e => setForm(prev => ({
                ...prev, email: e.target.value }))} />
            <TextField label="Om företaget" value={form.description} fullWidth
              multiline rows={4} disabled={!isAdmin}
              onChange={e => setForm(prev => ({
                ...prev, description: e.target.value }))} />
          </Box>
        </Paper>

      </Container>
    </Box>
  );
}